import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Clock, MapPin, Users, Globe, Mic, Lock, Coffee, UtensilsCrossed, GraduationCap, Loader2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import PremiumHero from '../components/PremiumHero';
import Footer from '../components/Footer';
import HiddenPageMessage from '../components/HiddenPageMessage';
import PreviewBanner from '../components/PreviewBanner';
import { useVisibility } from '../hooks/useVisibility';
import { usePreviewMode, fetchWithPreview } from '../hooks/usePreviewMode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback Program Data (used when CMS data is empty or unavailable)
const fallbackProgramData = {
  april1: [
    {
      id: 'utk-tour-1',
      time: 'Mid-day',
      title: 'UTK Nuclear Engineering Tour',
      type: 'Tour',
      location: 'University of Tennessee, Knoxville (UTK)',
      description: 'Prior to the Opening Welcome Reception',
      isHighlighted: false,
      icons: ['graduation']
    },
    {
      id: 'welcome-reception',
      time: '5:30 PM – 7:30 PM',
      title: 'Opening Welcome Reception',
      subtitle: 'Engaging Leaders, Innovators & the Nuclear Workforce of the Future',
      type: 'Reception',
      description: 'The opening reception brings together Congress participants—U.S. leaders, international guests, industry innovators, and academic partners—for an informal start to the World Nuclear Congress. Guests are invited to convene, reconnect, and engage in conversation while also meeting a select group of graduate students and researchers, offering a glimpse into emerging talent and ongoing work shaping the future nuclear workforce.',
      isHighlighted: true,
      icons: ['international', 'graduation']
    },
    {
      id: 'vip-dinner',
      time: '7:00 PM – 9:30 PM',
      title: 'VIP Private Reception & Dinner',
      type: 'VIP Event',
      location: 'Dr. Hash Hashemian, Host & Sponsor – Knoxville, TN (Transportation provided)',
      description: 'An invitation-only evening gathering senior U.S. and international leaders, speakers, and distinguished guests in a private setting. The dinner is designed to foster elevated trust, thoughtful dialogue, and meaningful exchange—allowing conversations to unfold naturally among peers who share responsibility for shaping the global nuclear future.',
      isHighlighted: true,
      icons: ['vip', 'international']
    }
  ],
  april2: {
    morning: [
      {
        id: 'registration',
        time: '7:00 AM',
        title: 'Registration Opens and Continental Breakfast',
        type: 'Logistics',
        isBreak: true
      },
      {
        id: 'welcome-wnc',
        time: '8:00 AM – 8:10 AM',
        title: 'Welcome from WNC',
        subtitle: 'Who we are. What we do. Your day at a glance.',
        type: 'Welcome',
        isHighlighted: false,
        icons: ['speaker']
      },
      {
        id: 'opening-plenary',
        time: '8:10 AM – 8:30 AM',
        title: 'Opening Plenary',
        subtitle: 'U.S. Nuclear Readiness and Global Leadership',
        type: 'Plenary',
        speakers: [
          { name: 'Chris Wright', title: 'U.S. Secretary of Energy', status: 'Invited' }
        ],
        isHighlighted: true,
        icons: ['speaker']
      },
      {
        id: 'opening-ceremony',
        time: '8:30 AM – 9:00 AM',
        title: 'Official Opening Ceremony',
        subtitle: 'Welcome from the State of Tennessee!',
        type: 'Ceremony',
        description: 'The Opening Ceremony formally launches the World Nuclear Congress, welcoming U.S., international, academic, and allied leadership. Including Members of Congress. Through brief remarks and shared perspectives, the ceremony underscores the importance of global cooperation, allied partnerships, and national leadership in advancing nuclear energy—highlighting how U.S. states, including Tennessee, are contributing to this broader international effort.',
        speakers: [
          { name: 'The Honorable Bill Lee', title: 'Governor of Tennessee', status: 'Invited' },
          { name: 'The Honorable Marsha Blackburn', title: 'U.S. Senator, Tennessee', status: 'Invited' },
          { name: 'The Honorable Bill Hagerty', title: 'U.S. Senator, Tennessee', status: 'Invited' },
          { name: 'The Honorable Chuck Fleischmann', title: 'U.S. Representative (TN-03) Chairman of House Energy & Water Appropriations', status: 'Invited' },
          { name: 'The Honorable Tim Burchett', title: 'U.S. Representative (TN-02)', status: 'Invited' },
          { name: 'Dr. Donde Plowman', title: 'Chancellor, University of Tennessee, Knoxville', status: 'Invited' }
        ],
        isHighlighted: true,
        icons: ['international', 'speaker']
      },
      {
        id: 'keynote-dinanno',
        time: '8:45 AM – 9:00 AM',
        title: 'Keynote Address',
        subtitle: 'Global Competition in Nuclear Energy and Implications for National Security',
        type: 'Keynote',
        speakers: [
          { name: 'Undersecretary of State Tom DiNanno', title: '', status: 'Invited' }
        ],
        isHighlighted: true,
        icons: ['speaker']
      },
      {
        id: 'state-spotlight-tn',
        time: '9:00 AM – 9:05 AM',
        title: 'State Spotlight – Tennessee',
        subtitle: 'Lessons, Launchpads and Leadership',
        type: 'State Spotlight',
        description: 'A brief, high-impact presentation highlighting nuclear progress in Tennessee.',
        speakers: [
          { name: 'Congressman Chuck Fleischmann', title: 'U.S. Representative (TN-03) Chairman of House Energy & Water Appropriations', status: 'Invited' },
          { name: 'Brandon Gibson', title: 'Chief Operating Officer, State of Tennessee', status: 'Invited' },
          { name: 'Brian Wirth', title: 'TVA Department Chair and UT–ORNL Governor\'s Chair Professor of Computational Nuclear Engineering, University of Tennessee, Knoxville', status: 'Invited' }
        ],
        icons: ['speaker']
      },
      {
        id: 'keynote-garrish',
        time: '9:05 AM – 9:15 AM',
        title: 'Keynote Address',
        subtitle: 'Moving U.S. Advanced Nuclear Technology Abroad',
        type: 'Keynote',
        speakers: [
          { name: 'Assistant Secretary Theodore "Ted" Garrish', title: 'U.S. Department of Energy Office of Nuclear Energy', status: 'Invited' }
        ],
        isHighlighted: true,
        icons: ['speaker']
      },
      {
        id: 'state-spotlight-tx',
        time: '9:15 AM – 9:20 AM',
        title: 'State Spotlight – Texas',
        subtitle: 'Lessons, Launchpads and Leadership',
        type: 'State Spotlight',
        description: 'A brief, high-impact presentation highlighting nuclear progress in Texas.',
        note: 'State invitations to Members of Congress from TX',
        icons: ['speaker']
      },
      {
        id: 'featured-allied-keynote',
        time: '9:20 AM – 9:45 AM',
        title: 'Featured Allied Nation Keynote Address',
        subtitle: 'From Partnership to Powerhouse: Romania\'s Role in the Global Nuclear Future',
        type: 'Keynote',
        description: 'Key developments: Romania is leading the deployment of U.S.-developed SMR technology (NuScale) — the Doicesti SMR project is one of the most advanced in Europe. The U.S. government, through EXIM Bank, DFC, and USTDA, is providing historic financing and technical support. Romania is a founding member of the SMR Strategic Partnership, along with the U.S., Japan, UK, and others. Romania is working closely with NATO and EU allies to strengthen regional energy security and independence from Russian energy. Romania will host an SMR Training Academy, with support from U.S. national labs.',
        speakers: [
          { name: 'Cosmin Ghita', title: 'CEO of SN Nuclearelectrica, Romania', status: 'Confirmed' }
        ],
        isHighlighted: true,
        icons: ['speaker', 'international']
      },
      {
        id: 'session-1',
        time: '9:45 AM – 10:30 AM',
        title: 'Global Deployment Summit',
        subtitle: 'Allied Nations, Energy Security & the Path Forward',
        type: 'Summit',
        sessionNumber: 'I',
        description: 'This summit brings together allied nations, international institutions, and U.S. leadership to examine how nuclear energy supports energy security, resilience, and geopolitical stability. Through shared experience and policy dialogue, participants explore how allied cooperation accelerates deployment, strengthens supply chains, and advances collective security goals.',
        speakers: [
          { name: 'Aleshia Duncan', title: 'Deputy Assistant Secretary for International Cooperation – U.S. Department of Energy Office of Nuclear Energy', role: 'Moderator', status: 'Invited' },
          { name: 'Adam O\'Malley', title: 'Director for Energy and Environmental Industries - Department of Commerce, International Trade Administration', role: 'Moderator', status: 'Invited' },
          { name: 'William D. Magwood, IV', title: 'NEA, Director General - OECD Nuclear Energy Agency', status: 'Invited' },
          { name: 'Dr. Sunil Felix', title: 'Nuclear Counsellor at the French Embassy', status: 'Confirmed' },
          { name: 'Florence Low Lee', title: 'President and CEO, Global American Business Institute (GABI)', status: 'Invited' },
          { name: 'Patricia Schouker', title: 'Transition Team Leader – British Embassy Washington', status: 'Invited' },
          { name: 'Sama Bilbao y Leon', title: 'Director General WNA', status: 'Invited' },
          { name: 'H.E. Bogdan-Gruia Ivan', title: 'Minister of Energy, Romania', status: 'Invited' },
          { name: 'Yukiko Tokuya', title: 'First Secretary, Embassy of Japan', status: 'Invited' },
          { name: 'Ana-Maria Petrescu', title: 'Political Officer, Embassy of Romania', status: 'Invited' }
        ],
        note: 'Representatives from France, UK, Japan, Poland, Canada, and Romania.',
        isHighlighted: true,
        icons: ['international', 'speaker']
      },
      {
        id: 'coffee-1',
        time: '10:30 AM – 11:00 AM',
        title: 'Coffee & Conversation',
        subtitle: 'Break & Engagement Showcase',
        type: 'Break',
        description: 'A dynamic networking break designed to encourage direct engagement through brief bilateral meetings, interactions with showcasing organizations, and scheduled conversations with graduate students—connecting global leadership with innovation, expertise, and future talent.',
        isBreak: true,
        icons: ['coffee']
      }
    ],
    midday: [
      {
        id: 'session-2',
        time: '11:00 AM – 12:30 PM',
        title: 'Global Lessons, U.S. Builds & Fuel Readiness',
        subtitle: 'A Three-Part Growing Ecosystem Super Panel',
        type: 'Super Panel',
        sessionNumber: 'II',
        description: 'This integrated session examines the nuclear deployment ecosystem as a connected whole—linking international experience, U.S. first builds, and fuel-cycle readiness. Designed to move beyond siloed discussions, the session highlights how execution, scaling, and infrastructure must advance together to achieve timely, secure nuclear deployment. This session is anchored by national laboratory leadership reflecting the complementary roles of the U.S. nuclear laboratory system in deployment and scale-up.',
        anchors: [
          { name: 'John C. Wagner, Ph.D.', title: 'Director, Idaho National Laboratory (INL)', description: 'brings a deployment and demonstration lens—highlighting how international lessons are translated into U.S. first-of-a-kind projects and de-risked pathways from FOAK to fleet deployment. Highlighting National labs role in supporting U.S. deployment of advanced new nuclear technologies.', status: 'Invited' },
          { name: 'Dr. Stephen K. Streiffer', title: 'Director, Oak Ridge National Laboratory (ORNL)', description: 'provides a scaling and infrastructure perspective—focusing on materials, manufacturing readiness, workforce development, and the systems required to move from initial builds to sustained fleet operation. Highlighting National labs role in supporting U.S. deployment of advanced new nuclear technologies.', status: 'Invited' }
        ],
        moderator: { name: 'Dr. Ron S. Faibish', title: 'Sr. Director Strategic Development Nuclear Technologies and Materials, General Atomics', status: 'Confirmed' },
        parts: [
          { 
            title: 'Part I — Global Nuclear Experience: A Shared Path Forward', 
            description: 'This discussion draws on international nuclear programs to highlight lessons learned in execution, governance, and delivery. Global operators and fuel-cycle leaders share what has worked—and what has not—providing insights that inform U.S. demonstration projects and deployment strategies.',
            speakers: [
              { name: 'John Hopkins', title: 'CEO NuScale', status: 'Invited' },
              { name: 'Roger Martella', title: 'GE Vernova', status: 'Invited' },
              { name: 'Dan Lipman', title: 'President for Energy Systems – Westinghouse Electric Company', status: 'Invited' },
              { name: 'Cosmin Ghita', title: 'CEO of SN Nuclearelectrica - Romania', status: 'Confirmed' }
            ]
          },
          { 
            title: 'Part II — U.S. Deployment Reality: From First Builds to Fleet Scale', 
            description: 'Focused on the U.S. deployment landscape, this session examines the transition from first-of-a-kind projects to fleet-scale deployment. Panelists explore challenges related to materials, manufacturing, workforce development, and supply chains, emphasizing the importance of scaling in parallel with deployment.',
            speakers: [
              { name: 'Chris Levesque', title: 'President & CEO – TerraPower', status: 'Invited' },
              { name: 'Dr. Chris Singh', title: 'President & CEO – Holtec International', status: 'Invited' },
              { name: 'Clay Sell', title: 'President & CEO – X-Energy', status: 'Invited' }
            ]
          },
          { 
            title: 'Part III — Fueling the Fleet: The Infrastructure Behind Deployment', 
            description: 'This session positions nuclear fuel as critical infrastructure rather than a supporting detail. Participants examine enrichment, fabrication, qualification, and supply resilience, connecting fuel readiness directly to deployment timelines, manufacturing capacity, and national security objectives.',
            speakers: [
              { name: 'Seth Grae', title: 'President & CEO, Lightbridge Corporation', status: 'Confirmed' },
              { name: 'Jean-Luc Palayer', title: 'President & CEO, Orano USA', status: 'Pre-confirmed' },
              { name: 'Sean Oehlbert', title: 'Vice-President Corporate Business Strategy – Centrus Energy Corporation', status: 'Invited' }
            ]
          }
        ],
        note: 'Together, INL and ORNL frame the discussion as an integrated ecosystem—linking execution, scale, and fuel readiness into a cohesive deployment strategy.',
        icons: ['speaker', 'international']
      },
      {
        id: 'session-3',
        time: '12:30 PM – 1:30 PM',
        title: 'Lunch & Learn: Distinguished Featured Speakers',
        type: 'Lunch & Learn',
        sessionNumber: 'III',
        description: 'A curated mid-day session offering strategic insights from global and U.S. thought leaders. Designed to be both informative and accessible, the Lunch & Learn connects policy research, international innovation, and market dynamics with practical implications for U.S. nuclear leadership. (12:30 – 12:50 buffet | 12:50 – 1:30 Speakers)',
        speakers: [
          { name: 'Doreen Abeysundra', title: 'Founder and Principal, Fresco CleanTech Solutions, London England, United Kingdom and WINFIN (Women in Nuclear – Finance) creator', status: 'Confirmed' },
          { name: 'Sam Thernstrom', title: 'Founder and President of The Energy Innovation Reform Project', status: 'Pre-confirmed' },
          { name: 'Paul Saunders', title: 'President of The Center for National Interest', status: 'Pre-confirmed' }
        ],
        icons: ['speaker', 'meals']
      }
    ],
    afternoon: [
      {
        id: 'session-4',
        time: '1:30 PM – 2:00 PM',
        title: 'Inside the Hill Studio',
        subtitle: 'Governing the Nuclear Moment',
        type: 'Inside the Hill Studio',
        sessionNumber: 'IV',
        studioDescription: 'Inside the Hill Studio is a signature program of the Congressional Energy Engagement Foundation, designed to move beyond traditional panels and offer a more personal, conversational view of leadership. These interview-style discussions explore the motivations, responsibilities, and decision-making behind public service—connecting policy, governance, and real-world impact through candid dialogue.',
        description: 'This special joint conversation examines how governance, oversight, and leadership intersect at a defining moment for nuclear energy, offering insight into how decisions made today shape long-term outcomes. A live recorded session.',
        note: 'Senator Risch - Int\'l Nuclear Energy Act – Chairman of the Senate Foreign Relations Committee',
        speakers: [
          { name: 'Michelle Amante-Harstine', title: 'CEO, Congressional Energy Engagement', role: 'Host', status: 'Confirmed' },
          { name: 'Hash Hashemian', title: '', role: 'Co-host', status: 'Confirmed' },
          { name: 'Ho Nieh', title: 'Chairman U.S. Nuclear Regulatory Commission', status: 'Pre-confirmed' },
          { name: 'James E. Risch', title: 'U.S. Senator for Idaho', status: 'Invited' },
          { name: 'Charles J. Fleischmann', title: 'U.S. Congressman TN-03 and Chairman of the House Energy and Water Subcommittee on Appropriations', status: 'Confirmed' }
        ],
        isHighlighted: true,
        icons: ['speaker']
      },
      {
        id: 'session-5',
        time: '2:00 PM – 2:30 PM',
        title: 'Inside the Hill Studio – Project Edition',
        subtitle: 'Romania: The WNC Featured Allied Nation - A Case Study',
        type: 'Inside the Hill Studio',
        sessionNumber: 'V',
        description: 'This fireside interview-style conversation highlights Romania\'s nuclear program and U.S.–Romanian cooperation as a model of long-term allied partnership. Through a candid exchange, the session explores deployment strategy, fuel-cycle collaboration, and the value of trusted bilateral relationships in advancing shared energy and security goals from a personal as well as professional view.',
        speakers: [
          { name: 'Michelle Amante-Harstine', title: 'CEO, Congressional Energy Engagement', role: 'Host', status: 'Confirmed' },
          { name: 'Dr. Kirsten Cutler', title: 'Senior Strategist for Nuclear Innovation, U.S. Department of State', role: 'Co-host', status: 'Confirmed' },
          { name: 'Cosmin Ghiță', title: 'Chief Executive Officer, Nuclearelectrica Romania', status: 'Confirmed' },
          { name: 'Todd Abrajano', title: 'President & CEO, U.S. Nuclear Industry Council', status: 'Pre-confirmed' },
          { name: 'Carl Kress', title: 'Regional Director & Global Energy Industry Team Co-Lead - USTDA', status: 'Invited' },
          { name: 'David Marventano', title: 'Senior Vice President Government Relations, Fluor', status: 'Invited' }
        ],
        note: 'USTDA provided the funds for the initial SMR feasibility study that got the SMR project in Romania launched. Fluor has done three years and a few hundred million in work at the site.',
        isHighlighted: true,
        icons: ['speaker', 'international']
      },
      {
        id: 'state-spotlight-in',
        time: '2:30 PM – 2:35 PM',
        title: 'State Spotlight – Indiana',
        subtitle: 'Lessons, Launchpads and Leadership',
        type: 'State Spotlight',
        description: 'A brief, high-impact presentation highlighting nuclear progress in Indiana.',
        speakers: [
          { name: 'Victoria Spartz', title: 'U.S. Representative (IN-05)', status: 'Invited' },
          { name: 'Suzie Jaworowski', title: 'Secretary of Energy and Natural Resources for the state of Indiana', status: 'Invited' }
        ],
        icons: ['speaker']
      },
      {
        id: 'coffee-2',
        time: '2:35 PM – 3:00 PM',
        title: 'Coffee & Conversation',
        subtitle: 'Break & Engagement Showcase',
        type: 'Break',
        description: 'A dynamic networking break designed to encourage direct engagement through brief bilateral meetings, interactions with showcasing organizations, and scheduled conversations with graduate students—connecting global leadership with innovation, expertise, and future talent.',
        isBreak: true,
        icons: ['coffee']
      },
      {
        id: 'session-6',
        time: '3:00 PM – 4:00 PM',
        title: 'Financing the Nuclear Renaissance',
        subtitle: 'From Development Banks to Deployment Capital',
        type: 'Panel',
        sessionNumber: 'VI',
        description: 'This session explores the financial frameworks required to support new nuclear deployment—from multilateral development banks to private and public capital. The discussion focuses on aligning policy, risk-sharing, and investment tools to move projects from concept to operation at scale.',
        moderator: { name: 'Dr. Kirsten Cutler', title: 'Senior Strategist for Nuclear Innovation, U.S. Department of State', status: 'Confirmed' },
        speakers: [
          { name: 'James (Jim) Schafer', title: 'Senior Managing Director – Guggenheim', status: 'Invited' },
          { name: 'Pierre Auinet', title: 'Lead Energy Specialist – World Bank, IBRD-IDA', status: 'Invited' },
          { name: 'Daniel Tobin', title: 'Vice President, Structured & Project Finance – Export-Import Bank of the United States (EXIM)', status: 'Invited' },
          { name: 'Tom Haslett', title: 'Director for Energy and Critical Minerals Policy – U.S. Development and Finance Corporation', status: 'Invited' },
          { name: 'Chris Colbert', title: 'CEO Elementl Power', status: 'Invited' }
        ],
        icons: ['speaker']
      },
      {
        id: 'state-spotlight-ky',
        time: '4:00 PM – 4:05 PM',
        title: 'State Spotlight – Kentucky',
        subtitle: 'Lessons, Launchpads and Leadership',
        type: 'State Spotlight',
        description: 'A brief, high-impact presentation highlighting nuclear progress in Kentucky.',
        icons: ['speaker']
      },
      {
        id: 'session-7',
        time: '4:05 PM – 5:00 PM',
        title: 'Nuclear for Defense & Deterrence',
        subtitle: 'The Strategic Imperative',
        type: 'Panel',
        sessionNumber: 'VII',
        description: 'This session examines the role of nuclear energy and infrastructure in national security, deterrence, and strategic stability. Drawing from defense, national security, regulatory, and laboratory perspectives, the discussion explores how nuclear capabilities underpin resilience in an increasingly complex global environment.',
        speakers: [
          { name: 'Brandon Williams', title: 'Administrator, U.S. National Nuclear Security Administration', status: 'Pre-confirmed', note: 'Setting the Stage Address' },
          { name: 'Rex D. Geveden', title: 'President and CEO, BWXT', status: 'Invited' },
          { name: 'Dr. Rich Tighe', title: 'President and CEO, Consolidated Nuclear Security (CNS)', status: 'Invited' }
        ],
        icons: ['speaker']
      }
    ],
    closing: [
      {
        id: 'session-8',
        time: '5:00 PM – 5:30 PM',
        title: 'International Alliance Closing Ceremony & Reception',
        subtitle: 'A Toast to Our Global Partners',
        type: 'Ceremony',
        sessionNumber: 'VIII',
        description: 'This closing session is a seated, table-top reception integrated into the final program hour, with wine and cheese service brought directly to attendees. Designed as a moment of recognition rather than departure, the session honors allied nations, embassy partners, and international participants—celebrating shared commitment, cooperation, and progress as global partners.',
        highlights: [
          'A Toast to Our Allies (Led by WNC, Sponsors and Featured Allied Nation - Romania)',
          'Closing Leadership Reflection & Appreciation',
          'Announcement of the 2027 WNC Featured Allied Nation – Passing the WNC Mantle'
        ],
        closingNote: 'The Congress concludes with leadership reflections emphasizing the shared responsibility of the United States and its allies in advancing nuclear energy for a safer, more resilient world. This moment recognizes international partners, dignitaries, and contributors; acknowledges U.S. state leaders and representatives in attendance; and thanks, the University of Tennessee as the host location for convening the Congress. Together, these acknowledgments reinforce how global collaboration and state-level leadership work hand in hand to uphold shared nuclear goals.',
        isHighlighted: true,
        icons: ['international']
      },
      {
        id: 'utk-tour-2',
        time: 'Post Event',
        title: 'UTK Nuclear Engineering Tour',
        type: 'Tour',
        description: 'An additional opportunity for interested guests—particularly those departing later in the day or unable to attend earlier—to tour UTK\'s Nuclear Engineering facilities.',
        icons: ['graduation']
      }
    ]
  }
};

const fallbackLogistics = [
  {
    id: 'location',
    title: 'Location & Venue',
    content: [
      'Primary Venue: University of Tennessee, Knoxville (UTK)',
      '1502 Cumberland Avenue, Knoxville, TN 37916',
      'Student Union Building',
      'Buildings: Student Union & Zeanah Engineering Complex (specific rooms will be noted in program)',
      'Campus is walkable'
    ]
  },
  {
    id: 'registration',
    title: 'Registration & Check-In',
    content: [
      'Registration Opens: 7:00 AM, Day 2',
      'Badge Required for all sessions and events',
      'Photo ID may be requested for select sessions or VIP access'
    ]
  },
  {
    id: 'credentials',
    title: 'Credentials & Access',
    content: [
      'General Attendee Badge: Plenary sessions, panels, engagement breaks, closing reception',
      'VIP Access: Private dinner, green room, select bilaterals',
      'Green Room: Reserved for speakers and invited leaders'
    ]
  },
  {
    id: 'dresscode',
    title: 'Dress Code',
    content: [
      'Daytime Program: Business',
      'VIP Private Dinner: Business formal / cocktail attire (not semi or formal)',
      'Welcome Reception & Closing Reception: Business or elevated business casual'
    ]
  },
  {
    id: 'travel',
    title: 'Travel & Transportation',
    content: [
      'Nearest Airport: McGhee Tyson Airport (TYS) – ~20 minutes from campus',
      'Ground Transportation: Uber, Lyft, taxis readily available',
      'VIP Transportation: Provided for private dinner participants (details shared separately)'
    ]
  },
  {
    id: 'hotels',
    title: 'Nearby Hotels',
    content: [
      'Graduate by Hilton Knoxville - 0.4 miles | +1 865-437-5500 (A boutique-style hotel adjacent to the UTK campus and Neyland Stadium — ideal for walking)',
      'Hilton Knoxville - 0.8 miles | +1 865-523-2300 (A full-service downtown hotel within University walking distance and close to Market Square)',
      'Hampton Inn & Suites Knoxville Downtown - 0.9 miles | +1 865-522-5400 (Comfortable mid-range option in the downtown historic district)',
      'Embassy Suites by Hilton Knoxville Downtown - 1.0 miles | +1 865-544-8502 (All-suite hotel with breakfast included and a rooftop bar)',
      'Marriott Knoxville Downtown - 1.1 miles | +1 865-523-5000',
      'The Tennessean Hotel - 0.9 miles | +1 865-232-1800 (A luxury downtown hotel on the Tennessee Riverwalk, elevated stay experience for VIPs)'
    ]
  },
  {
    id: 'meals',
    title: 'Meals & Refreshments',
    content: [
      'Enhanced Continental Breakfast (Day 2)',
      'Lunch & Learn (buffet-style lunch provided)',
      'Coffee service available throughout the morning and afternoon',
      'Closing Wine & Cheese Reception served tableside during the final session (Planned)',
      'Dietary needs accommodated if noted during registration'
    ]
  },
  {
    id: 'engagement',
    title: 'Engagement, Bilaterals & Showcases',
    content: [
      'Engagement Showcase & Bilaterals occur during scheduled breaks',
      'Graduate student appointments are optional and pre-scheduled',
      'Showcase spaces are curated and limited'
    ]
  },
  {
    id: 'format',
    title: 'Program Format Notes',
    content: [
      'Sessions are designed for direct engagement and discussion, not long lectures',
      'Several sessions use interview-style or fireside formats',
      'Some sessions may run precisely on time to accommodate VIP schedules',
      'Engagement is priority'
    ]
  },
  {
    id: 'media',
    title: 'Photography & Media',
    content: [
      'Professional photography and videography will be present',
      'By attending, participants consent to non-commercial event photography',
      'Media interviews arranged by request'
    ]
  },
  {
    id: 'security',
    title: 'Security & Conduct',
    content: [
      'This is a high-trust professional environment',
      'Respectful dialogue and professional conduct are expected',
      'Some discussions may be off-the-record—this will be clearly indicated'
    ]
  },
  {
    id: 'contact',
    title: 'Questions & Assistance',
    content: [
      'Event Team On-Site: Registration desk & information staff available all day',
      'Pre-Event Questions: General inquiries via registration confirmation',
      'VIP inquiries: Michelle@CEELeaders.com or +1 423-421-2825'
    ]
  }
];

// Session Card Component
const SessionCard = ({ session, isOpen, onToggle }) => {
  const getTypeColor = (type) => {
    const colors = {
      'Ceremony': 'bg-amber-100 text-amber-800',
      'Plenary': 'bg-blue-100 text-blue-800',
      'Summit': 'bg-purple-100 text-purple-800',
      'Panel': 'bg-green-100 text-green-800',
      'Super Panel': 'bg-indigo-100 text-indigo-800',
      'Inside the Hill Studio': 'bg-rose-100 text-rose-800',
      'Lunch & Learn': 'bg-orange-100 text-orange-800',
      'Reception': 'bg-teal-100 text-teal-800',
      'VIP Event': 'bg-yellow-100 text-yellow-800',
      'Tour': 'bg-cyan-100 text-cyan-800',
      'Roundtable': 'bg-emerald-100 text-emerald-800',
      'Break': 'bg-gray-100 text-gray-600',
      'Logistics': 'bg-gray-100 text-gray-600',
      'Welcome': 'bg-sky-100 text-sky-800',
      'Keynote': 'bg-amber-100 text-amber-800',
      'State Spotlight': 'bg-lime-100 text-lime-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    if (status === 'Confirmed') return 'text-green-600';
    if (status === 'Pre-confirmed') return 'text-blue-600';
    return 'text-amber-600';
  };

  const renderIcon = (icon) => {
    switch (icon) {
      case 'speaker': return <Mic className="w-4 h-4" />;
      case 'international': return <Globe className="w-4 h-4" />;
      case 'vip': return <Lock className="w-4 h-4" />;
      case 'coffee': return <Coffee className="w-4 h-4" />;
      case 'meals': return <UtensilsCrossed className="w-4 h-4" />;
      case 'graduation': return <GraduationCap className="w-4 h-4" />;
      default: return null;
    }
  };

  if (session._placeholder) {
    return (
      <div
        data-testid={`session-placeholder-${session.id}`}
        className="flex items-center gap-4 py-5 px-6 bg-slate-50 rounded-xl border border-dashed border-slate-300"
      >
        <div className="flex items-center gap-2 text-[#00457f] min-w-[140px]">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span className="font-semibold text-sm md:text-base">{session.time}</span>
        </div>
        <span className="italic text-slate-500">{session.title}</span>
      </div>
    );
  }

  if (session.isBreak && !session.description) {
    return (
      <div className="flex items-center gap-4 py-4 px-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-gray-500">
          <Clock className="w-4 h-4" />
          <span className="font-medium">{session.time}</span>
        </div>
        <span className="text-gray-700">{session.title}</span>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-xl border transition-all duration-300 ${
        session.isHighlighted 
          ? 'border-[#00457f]/30 bg-gradient-to-r from-[#00457f]/5 to-transparent shadow-md' 
          : session.isBreak 
            ? 'border-gray-200 bg-gray-50'
            : 'border-gray-200 bg-white hover:shadow-md'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-5 md:p-6"
        aria-expanded={isOpen}
      >
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          {/* Time */}
          <div className="flex items-center gap-2 text-[#00457f] min-w-[140px]">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span className="font-semibold text-sm md:text-base">{session.time}</span>
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {session.sessionNumber && (
                <span className="text-xs font-bold text-gray-400">SESSION {session.sessionNumber}</span>
              )}
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getTypeColor(session.type)}`}>
                {session.type}
              </span>
              {session.room && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: session.room.color || '#00457f' }}
                  title={session.room.description || session.room.name}
                  data-testid={`session-room-pill-${session.id}`}
                >
                  <MapPin className="w-3 h-3" /> {session.room.name}
                </span>
              )}
              {session.icons?.map((icon, idx) => (
                <span key={idx} className="text-gray-400">{renderIcon(icon)}</span>
              ))}
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">{session.title}</h3>
            {session.subtitle && (
              <p className="text-gray-600 text-sm md:text-base">{session.subtitle}</p>
            )}
            {session.location && (
              <p className="text-gray-500 text-sm mt-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {session.location}
              </p>
            )}
          </div>

          {/* Expand Icon */}
          <div className="flex-shrink-0 text-gray-400">
            {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {isOpen && (
        <div className="px-5 md:px-6 pb-6 border-t border-gray-100 pt-4">
          {session.studioDescription && (
            <div className="bg-rose-50 rounded-lg p-4 mb-4 text-sm text-gray-700 italic">
              <strong>About Inside the Hill Studio:</strong> {session.studioDescription}
            </div>
          )}

          {session.description && (
            <p className="text-gray-700 leading-relaxed mb-4">{session.description}</p>
          )}

          {session.note && (
            <p className="text-gray-600 text-sm italic mb-4">{session.note}</p>
          )}

          {session.keynote && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Keynote</p>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="font-semibold text-gray-900">{session.keynote.name}</p>
                <p className="text-sm text-gray-600">{session.keynote.title}</p>
                <span className={`text-xs font-medium ${getStatusColor(session.keynote.status)}`}>
                  {session.keynote.status}
                </span>
              </div>
            </div>
          )}

          {session.anchors && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Session Anchors</p>
              <div className="space-y-3">
                {session.anchors.map((anchor, idx) => (
                  <div key={idx} className="bg-blue-50 rounded-lg p-3">
                    <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
                      <p className="font-semibold text-gray-900">{anchor.name}</p>
                      {anchor.status && (
                        <span className={`text-xs font-medium ${getStatusColor(anchor.status)}`}>
                          ({anchor.status})
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{anchor.title}</p>
                    {anchor.note && (
                      <p className="text-xs text-amber-600 italic mt-1">{anchor.note}</p>
                    )}
                    <p className="text-sm text-gray-700 mt-1">{anchor.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {session.parts && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Session Parts</p>
              <div className="space-y-3">
                {session.parts.map((part, idx) => (
                  <div key={idx} className="border-l-4 border-[#00457f] pl-4">
                    <p className="font-semibold text-gray-900">{part.title}</p>
                    <p className="text-sm text-gray-700 mt-1">{part.description}</p>
                    {part.speakers && part.speakers.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {part.speakers.map((speaker, sIdx) => (
                          <div key={sIdx} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                            <span className="font-medium text-gray-800">{speaker.name}</span>
                            {speaker.title && <span className="text-gray-600">– {speaker.title}</span>}
                            <span className={`text-xs font-medium ${getStatusColor(speaker.status)}`}>
                              {speaker.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {session.moderator && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Moderator</p>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-900">{session.moderator.name}</span>
                <span className="text-gray-600 text-sm">– {session.moderator.title}</span>
                <span className={`text-xs font-medium ${getStatusColor(session.moderator.status)}`}>
                  ({session.moderator.status})
                </span>
              </div>
            </div>
          )}

          {session.speakers && session.speakers.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Speakers</p>
              <div className="space-y-2">
                {session.speakers.map((speaker, idx) => (
                  <div key={idx} className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-gray-900">{speaker.name}</span>
                    {speaker.title && <span className="text-gray-600 text-sm">– {speaker.title}</span>}
                    {speaker.role && <span className="text-[#00457f] text-sm font-medium">({speaker.role})</span>}
                    <span className={`text-xs font-medium ${getStatusColor(speaker.status)}`}>
                      {speaker.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {session.highlights && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Highlights</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {session.highlights.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {session.closingNote && (
            <p className="text-gray-700 leading-relaxed italic border-l-4 border-amber-400 pl-4 bg-amber-50 py-3 rounded-r-lg">
              {session.closingNote}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Logistics Card Component
const LogisticsCard = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-center justify-between"
        aria-expanded={isOpen}
      >
        <h4 className="font-semibold text-gray-900">{item.title}</h4>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          <ul className="space-y-2">
            {item.content.map((line, idx) => (
              <li key={idx} className="text-gray-700 text-sm">{line}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Transform CMS data to component format
const transformCMSData = (cmsData, speakersMap = {}, hiddenNames = new Set()) => {
  if (!cmsData || !cmsData.days || cmsData.days.length === 0) {
    return null;
  }

  const day1 = cmsData.days.find(d => d.day_number === 1);
  const day2 = cmsData.days.find(d => d.day_number === 2);

  // Build a placeholder card for a whole-day being hidden. Keeps the date label visible so
  // the public sees the day still exists on the schedule.
  const dayPlaceholder = (day, keyPrefix) => ([{
    id: `${keyPrefix}-hidden-day-placeholder`,
    time: day?.date || '',
    title: 'Program details coming soon',
    subtitle: null,
    type: null,
    description: null,
    location: null,
    isHighlighted: false,
    isBreak: false,
    speakers: undefined,
    icons: [],
    _placeholder: true,
  }]);

  const day1Sessions = (day1?.visible === false)
    ? null
    : (day1?.sessions || []);
  const day2Sessions = (day2?.visible === false)
    ? null
    : (day2?.sessions || []);

  // Test a plain-text speaker string against the hidden-names set.
  // Match if the hidden speaker's name appears as a substring of the text (case-insensitive),
  // which handles CMS strings like "Chris Wright, U.S. Secretary of Energy".
  const textMatchesHidden = (text) => {
    if (!text || hiddenNames.size === 0) return false;
    const lower = String(text).toLowerCase();
    for (const hn of hiddenNames) {
      if (hn && lower.includes(hn)) return true;
    }
    return false;
  };

  const transformSession = (session) => {
    const time = session.end_time
      ? (session.start_time ? `${session.start_time} – ${session.end_time}` : session.end_time)
      : (session.start_time || 'Time TBD');

    // Hidden session → keep the time slot, replace details with placeholder.
    if (session.visible === false) {
      return {
        id: session.id,
        time,
        title: 'Details coming soon',
        subtitle: null,
        type: null,
        description: null,
        location: null,
        isHighlighted: false,
        isBreak: false,
        speakers: undefined,
        icons: [],
        _placeholder: true,
      };
    }

    const sessionTypeMap = {
      'keynote': 'Keynote',
      'panel': 'Panel',
      'ceremony': 'Ceremony',
      'networking': 'Networking',
      'tour': 'Tour',
      'reception': 'Reception',
      'break': 'Break',
      'lunch': 'Lunch & Learn'
    };

    // Resolve speakers from speaker_ids (linked to speakers database)
    let resolvedSpeakers = [];
    if (session.speaker_ids?.length > 0) {
      resolvedSpeakers = session.speaker_ids
        .map(id => speakersMap[id])
        .filter(Boolean)
        .map(s => ({ 
          name: s.name, 
          title: s.title || '', 
          organization: s.organization || '',
          status: s.status || 'Invited',
          image_url: s.image_url
        }));
    } else if (session.speakers?.length > 0) {
      // Fallback to plain text speakers array (legacy format).
      // Filter out any string that mentions a hidden speaker's name so hiding in the admin
      // panel takes effect on the public program even for legacy sessions.
      resolvedSpeakers = session.speakers
        .filter(s => !textMatchesHidden(s))
        .map(s => ({ name: s, title: '', status: 'Invited' }));
    }

    return {
      id: session.id,
      time,
      title: session.title,
      subtitle: session.subtitle || null,
      type: sessionTypeMap[session.session_type] || session.session_type,
      description: session.description || null,
      location: session.location || null,
      room_id: session.room_id || null,
      isHighlighted: session.highlighted || false,
      isBreak: session.session_type === 'break',
      speakers: resolvedSpeakers.length > 0 ? resolvedSpeakers : undefined,
      icons: getIconsForSession(session.session_type)
    };
  };

  const getIconsForSession = (sessionType) => {
    const iconMap = {
      'keynote': ['speaker'],
      'panel': ['speaker'],
      'ceremony': ['speaker', 'international'],
      'reception': ['international'],
      'tour': ['graduation'],
      'break': ['coffee'],
      'lunch': ['speaker', 'meals']
    };
    return iconMap[sessionType] || [];
  };

  // Day 1 hidden → single placeholder card; otherwise transform all sessions.
  const april1 = (day1Sessions === null)
    ? dayPlaceholder(day1, 'day1')
    : day1Sessions.map(transformSession);

  // Day 2 hidden → single placeholder card in the morning slot; other buckets empty.
  const day2Buckets = (day2Sessions === null)
    ? { morning: dayPlaceholder(day2, 'day2'), midday: [], afternoon: [], closing: [] }
    : {
        morning: day2Sessions.filter(s => {
          const hour = parseInt(s.start_time?.split(':')[0] || '0');
          return hour < 12 && hour >= 7;
        }).map(transformSession),
        midday: day2Sessions.filter(s => {
          const hour = parseInt(s.start_time?.split(':')[0] || '0');
          const isPM = s.start_time?.toLowerCase().includes('pm');
          return (hour === 11 || hour === 12 || (hour >= 1 && hour < 2 && isPM));
        }).map(transformSession),
        afternoon: day2Sessions.filter(s => {
          const hour = parseInt(s.start_time?.split(':')[0] || '0');
          const isPM = s.start_time?.toLowerCase().includes('pm');
          return hour >= 2 && hour < 5 && isPM;
        }).map(transformSession),
        closing: day2Sessions.filter(s => {
          const hour = parseInt(s.start_time?.split(':')[0] || '0');
          const isPM = s.start_time?.toLowerCase().includes('pm');
          return (hour >= 5 && isPM) || s.start_time?.toLowerCase().includes('post');
        }).map(transformSession),
      };

  return {
    april1,
    april2: day2Buckets,
  };
};

const transformLogistics = (cmsLogistics) => {
  if (!cmsLogistics || cmsLogistics.length === 0) {
    return null;
  }
  return cmsLogistics.map(item => ({
    id: item.id,
    title: item.title,
    content: item.content?.split('\n').filter(Boolean) || [item.content]
  }));
};

// Map backend session_type → human-readable label used by SessionCard color map
const SESSION_TYPE_LABEL = {
  keynote: 'Keynote',
  panel: 'Panel',
  ceremony: 'Ceremony',
  networking: 'Networking',
  tour: 'Tour',
  reception: 'Reception',
  break: 'Break',
  lunch: 'Lunch & Learn',
};
const prettifySessionType = (s) =>
  s.type || SESSION_TYPE_LABEL[s.session_type] || s.session_type || null;

// RoomGrid — side-by-side columns per room, with one row per session (sorted by time).
// Keeps the SessionCard look within each cell. "No room" sessions fall into a trailing
// column when present so nothing is hidden in grid view.
const RoomGrid = ({ sessions, rooms, hasUnroomed, sessionPlaceholderText, showHiddenInPreview, openSession, onToggle }) => {
  // Group sessions by room_id (or 'none')
  const columns = [...rooms.map(r => ({ key: r.id, room: r })), ...(hasUnroomed ? [{ key: 'none', room: null }] : [])];
  const groups = {};
  for (const col of columns) groups[col.key] = [];
  for (const s of sessions) {
    const k = s.room_id || 'none';
    if (groups[k]) groups[k].push(s);
  }
  // Determine max column height for consistent row heights (optional; current CSS just stacks)
  return (
    <div
      className="grid gap-4 overflow-x-auto"
      style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(260px, 1fr))` }}
      data-testid="program-room-grid"
    >
      {columns.map((col) => (
        <div key={col.key} className="flex flex-col gap-3 min-w-0" data-testid={`room-column-${col.key}`}>
          <div
            className="sticky top-0 z-[1] inline-flex items-center gap-1 self-start px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow"
            style={{ backgroundColor: col.room?.color || '#334155' }}
          >
            <MapPin className="w-3 h-3" />
            {col.room ? col.room.name : 'No room'}
            {col.room?.capacity ? <span className="opacity-80">· cap {col.room.capacity}</span> : null}
          </div>
          <div className="space-y-3">
            {groups[col.key].length === 0 ? (
              <div className="text-center text-xs text-gray-400 italic py-4 border border-dashed border-gray-200 rounded-lg">
                No sessions
              </div>
            ) : (
              groups[col.key].map((session) => {
                const timeVal = session.time_display
                  || session.time
                  || (session.end_time
                        ? (session.start_time ? `${session.start_time} – ${session.end_time}` : session.end_time)
                        : (session.start_time || 'Time TBD'));
                const isSessionHidden = session.visible === false && !showHiddenInPreview;
                const isPlaceholder = session._placeholder || isSessionHidden;
                return (
                  <SessionCard
                    key={session.id}
                    session={{
                      id: session.id,
                      time: timeVal,
                      title: session._placeholder
                        ? session.title
                        : (isSessionHidden ? sessionPlaceholderText : session.title),
                      subtitle: isPlaceholder ? null : session.subtitle,
                      type: isPlaceholder ? null : prettifySessionType(session),
                      location: isPlaceholder ? null : session.location,
                      room: isPlaceholder ? null : col.room,
                      description: isPlaceholder ? null : session.description,
                      isHighlighted: !isPlaceholder && session.is_highlighted,
                      isBreak: !isPlaceholder && session.is_break,
                      speakers: isPlaceholder ? undefined : session.speakers,
                      icons: isPlaceholder ? [] : (session.icons || []),
                      _placeholder: isPlaceholder,
                    }}
                    isOpen={openSession === session.id}
                    onToggle={() => onToggle(session.id)}
                  />
                );
              })
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Program Page
const ProgramPage = () => {
  const navigate = useNavigate();
  const [openSession, setOpenSession] = useState(null);
  const [openLogistics, setOpenLogistics] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState(fallbackProgramData);
  const [logistics, setLogistics] = useState(fallbackLogistics);
  const [cmsContent, setCmsContent] = useState(null);
  const [isDraft, setIsDraft] = useState(false);
  const [rooms, setRooms] = useState([]);
  // Per-day room filters and view mode (list vs grid)
  const [roomFilterByDay, setRoomFilterByDay] = useState({});   // { dayNumber: roomId | 'all' }
  const [viewModeByDay, setViewModeByDay] = useState({});       // { dayNumber: 'list' | 'grid' }
  
  // Check page visibility
  const { isPageVisible, isSectionVisible, getPageHiddenMessage, loading: visibilityLoading } = useVisibility();
  const { preview } = usePreviewMode();

  // Fetch CMS content and speakers data
  useEffect(() => {
    let ignore = false;
    const fetchContent = async () => {
      try {
        // Fetch program content (preview-aware) and speakers + hidden-names in parallel.
        // hidden-names is fetched in ALL modes (including preview) so preview mirrors
        // the live site's visibility rules exactly.
        const [programResult, speakersResponse, hiddenNamesResponse, roomsResponse] = await Promise.all([
          fetchWithPreview(`${API_URL}/api/content/program`, { previewEnabled: preview }),
          fetch(`${API_URL}/api/speakers${preview ? '?preview=1' : ''}`, preview ? { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } } : {}),
          fetch(`${API_URL}/api/speakers/hidden-names`),
          fetch(`${API_URL}/api/content/program/rooms`),
        ]);
        if (ignore) return;

        // Rooms (used for pill + filter buttons + grid columns)
        if (roomsResponse && roomsResponse.ok) {
          try {
            setRooms(await roomsResponse.json());
          } catch { /* non-fatal */ }
        }

        // Build speakers map for quick lookup by ID
        let speakersMap = {};
        if (speakersResponse.ok) {
          const speakersData = await speakersResponse.json();
          speakersMap = speakersData.reduce((acc, speaker) => {
            acc[speaker.id] = speaker;
            return acc;
          }, {});
        }

        // Build hidden-names set so legacy plain-text session.speakers strings can be filtered
        // out of both the live site AND admin preview.
        let hiddenNames = new Set();
        if (hiddenNamesResponse && hiddenNamesResponse.ok) {
          try {
            const j = await hiddenNamesResponse.json();
            hiddenNames = new Set((j.names || []).map((n) => (n || '').toLowerCase()));
          } catch { /* no-op */ }
        }

        const data = programResult.data;
        if (data) {
          setCmsContent(data);
          setIsDraft(programResult.isDraft);
          
          // Transform and use CMS data if available, otherwise use fallback
          // Pass speakersMap + hiddenNames to resolve/filter appropriately
          const transformedProgram = transformCMSData(data, speakersMap, hiddenNames);
          if (transformedProgram && transformedProgram.april1?.length > 0) {
            setProgramData(transformedProgram);
          }
          
          const transformedLogistics = transformLogistics(data.logistics);
          if (transformedLogistics && transformedLogistics.length > 0) {
            setLogistics(transformedLogistics);
          }
        }
      } catch (error) {
        console.error('Failed to fetch program content:', error);
        // Fallback data is already set as default
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
    return () => { ignore = true; };
  }, [preview]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'april-1', 'april-2', 'sessions', 'logistics'];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If page is hidden, show the hidden message (placed after all hooks)
  if (!visibilityLoading && !isPageVisible('program')) {
    const message = getPageHiddenMessage('program');
    return (
      <HiddenPageMessage
        header={message.header}
        subtitle={message.subtitle}
        body={message.body}
      />
    );
  }

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 120;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const toggleSession = (id) => {
    setOpenSession(openSession === id ? null : id);
  };

  const toggleLogistics = (id) => {
    setOpenLogistics(openLogistics === id ? null : id);
  };

  // Build navigation items dynamically from CMS days data
  const navItems = [
    { id: 'overview', label: 'Overview' },
    // Dynamically add day tabs from CMS
    ...(cmsContent?.days || []).map((day, index) => ({
      id: `day-${day.day_number}`,
      label: day.date || `Day ${day.day_number}`,
      dayNumber: day.day_number
    })),
    // Fallback if no CMS days
    ...(!cmsContent?.days?.length ? [
      { id: 'day-1', label: 'Day 1' },
      { id: 'day-2', label: 'Day 2' }
    ] : []),
    { id: 'logistics', label: 'Logistics & Travel' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </div>
    );
  }

  // Custom placeholder text (per-event admin control, with sensible defaults)
  const dayPlaceholderText = cmsContent?.day_placeholder_text || 'Program details coming soon';
  const sessionPlaceholderText = cmsContent?.session_placeholder_text || 'Details coming soon';
  // Admin-only preview override: `?show_hidden=1` reveals hidden items in preview mode.
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const showHiddenInPreview = preview && urlParams.get('show_hidden') === '1';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="program-page">
      <PreviewBanner isDraft={isDraft} draftMeta={isDraft ? { updated_at: new Date().toISOString() } : null} pageLabel="Program" />
      {/* Navigation */}
      <Navigation />

      {/* Hero Section - Premium Design */}
      <div id="overview">
        <PremiumHero
          title={cmsContent?.hero_title || 'Program Overview'}
          subtitle="The Independent U.S. Forum for Global Nuclear Progress"
          overline={cmsContent?.hero_subtitle || 'Coming February 2027 | University of Tennessee, Knoxville'}
          testId="program-hero-title"
          editScope={{ type: 'program' }}
        />
      </div>

      {/* Preliminary Notice */}
      {(cmsContent?.preliminary_notice || true) && (
        <section className="py-6 bg-amber-50 border-b border-amber-200">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p className="text-amber-800 font-medium" data-testid="preliminary-notice">
              <strong>PRELIMINARY PROGRAM</strong> — {cmsContent?.preliminary_notice || 'This is a partial speaking list and is subject to change. Speaker listings for each session may be expanded to include additional speakers and guests.'}
            </p>
          </div>
        </section>
      )}

      {/* Sticky Navigation */}
      <nav className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex overflow-x-auto scrollbar-hide gap-1 py-3">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSection === item.id
                    ? 'bg-[#00457f] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-gray-700 leading-relaxed mb-6">
            {cmsContent?.overview_text || "The World Nuclear Congress (WNC) is an independent, U.S.-based international forum for global nuclear progress. The WNC is convened by Congressional Energy Engagement (CEE) as its flagship international platform and brings together U.S. leadership, allied nations, and global participants—including policymakers, U.S. and global industry leaders, embassy representatives, national laboratories, and academic institutions—to advance nuclear energy deployment, energy security, and international collaboration."}
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            {cmsContent?.overview_text_2 || "By intentionally bringing global perspectives into a U.S. setting, the Congress \"brings the world home,\" creating a space where experienced international leaders and U.S. stakeholders engage directly, share insights, and work together to make meaningful, collective progress on the opportunities and challenges shaping nuclear energy today."}
          </p>
          {(cmsContent?.featured_nation || "Romania") && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <p className="text-gray-800">
                <strong>{cmsContent?.featured_nation_year || "2027"} Featured Allied Nation:</strong> {cmsContent?.featured_nation || "Romania"} will serve as the Featured Allied Nation, {cmsContent?.featured_nation_description || "reflecting its leadership in nuclear deployment and its strong relationship with the United States, while welcoming participation and perspectives from additional allied nations and embassy representatives throughout the Congress."}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Dynamic Day Sections from CMS */}
      {(cmsContent?.days || []).map((day, index) => {
        const dayHidden = day.visible === false && !showHiddenInPreview;
        const rawSessions = dayHidden
          ? [{
              id: `day-${day.day_number}-hidden-placeholder`,
              start_time: day.date || '',
              end_time: null,
              title: dayPlaceholderText,
              _placeholder: true,
            }]
          : (day.sessions || []);

        // Compute distinct rooms used in this day's sessions (for filter buttons + grid cols)
        const roomIdsForDay = Array.from(new Set(
          rawSessions.map(s => s.room_id).filter(Boolean)
        ));
        const roomsForDay = roomIdsForDay
          .map(id => rooms.find(r => r.id === id))
          .filter(Boolean);
        const hasUnroomed = rawSessions.some(s => !s.room_id && !s._placeholder);
        const activeRoomFilter = roomFilterByDay[day.day_number] || 'all';
        const activeViewMode = viewModeByDay[day.day_number] || 'list';

        // Apply room filter
        const visibleRaw = (activeRoomFilter === 'all')
          ? rawSessions
          : rawSessions.filter(s => {
              if (s._placeholder) return true;
              if (activeRoomFilter === 'none') return !s.room_id;
              return s.room_id === activeRoomFilter;
            });

        return (
        <section 
          key={day.day_number} 
          id={`day-${day.day_number}`} 
          className={`py-12 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
        >
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#00457f]/30"></div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#00457f]">
                {day.date || `Day ${day.day_number}`}
                {day.label && <span className="text-lg font-normal text-gray-500 ml-2">— {day.label}</span>}
              </h2>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#00457f]/30"></div>
            </div>

            {/* Room filter + view mode toggle */}
            {!dayHidden && roomsForDay.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-6" data-testid={`day-${day.day_number}-controls`}>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setRoomFilterByDay(prev => ({ ...prev, [day.day_number]: 'all' }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      activeRoomFilter === 'all'
                        ? 'bg-[#00457f] text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-[#00457f]'
                    }`}
                    data-testid={`day-${day.day_number}-room-all`}
                  >
                    All rooms
                  </button>
                  {roomsForDay.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setRoomFilterByDay(prev => ({ ...prev, [day.day_number]: r.id }))}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                        activeRoomFilter === r.id
                          ? 'text-white border-transparent'
                          : 'text-gray-700 bg-white border-gray-200 hover:border-[#00457f]'
                      }`}
                      style={activeRoomFilter === r.id ? { backgroundColor: r.color || '#00457f' } : undefined}
                      data-testid={`day-${day.day_number}-room-${r.id}`}
                    >
                      <MapPin className="w-3 h-3" /> {r.name}
                    </button>
                  ))}
                  {hasUnroomed && (
                    <button
                      onClick={() => setRoomFilterByDay(prev => ({ ...prev, [day.day_number]: 'none' }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                        activeRoomFilter === 'none'
                          ? 'bg-gray-700 text-white'
                          : 'bg-white text-gray-500 border border-dashed border-gray-300 hover:border-gray-500'
                      }`}
                      data-testid={`day-${day.day_number}-room-none`}
                    >
                      No room
                    </button>
                  )}
                </div>
                <div className="ml-auto inline-flex rounded-full border bg-white overflow-hidden">
                  <button
                    onClick={() => setViewModeByDay(prev => ({ ...prev, [day.day_number]: 'list' }))}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      activeViewMode === 'list' ? 'bg-[#00457f] text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`day-${day.day_number}-view-list`}
                  >
                    List
                  </button>
                  <button
                    onClick={() => setViewModeByDay(prev => ({ ...prev, [day.day_number]: 'grid' }))}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      activeViewMode === 'grid' ? 'bg-[#00457f] text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`day-${day.day_number}-view-grid`}
                  >
                    Grid
                  </button>
                </div>
              </div>
            )}

            {/* Sessions for this day */}
            {activeViewMode === 'grid' && roomsForDay.length > 0 && !dayHidden ? (
              <RoomGrid
                sessions={visibleRaw}
                rooms={roomsForDay}
                hasUnroomed={hasUnroomed}
                sessionPlaceholderText={sessionPlaceholderText}
                showHiddenInPreview={showHiddenInPreview}
                openSession={openSession}
                onToggle={toggleSession}
              />
            ) : (
            <div className="space-y-4">
              {visibleRaw.map((session) => {
                const timeVal = session.time_display
                  || session.time
                  || (session.end_time
                        ? (session.start_time ? `${session.start_time} – ${session.end_time}` : session.end_time)
                        : (session.start_time || 'Time TBD'));
                const isSessionHidden = session.visible === false && !showHiddenInPreview;
                const isPlaceholder = session._placeholder || isSessionHidden;
                const assignedRoom = session.room_id ? rooms.find(r => r.id === session.room_id) : null;
                return (
                  <SessionCard
                    key={session.id}
                    session={{
                      id: session.id,
                      time: timeVal,
                      title: session._placeholder
                        ? session.title
                        : (isSessionHidden ? sessionPlaceholderText : session.title),
                      subtitle: isPlaceholder ? null : session.subtitle,
                      type: isPlaceholder ? null : prettifySessionType(session),
                      location: isPlaceholder ? null : session.location,
                      room: isPlaceholder ? null : assignedRoom,
                      description: isPlaceholder ? null : session.description,
                      isHighlighted: !isPlaceholder && session.is_highlighted,
                      isBreak: !isPlaceholder && session.is_break,
                      speakers: isPlaceholder ? undefined : session.speakers,
                      icons: isPlaceholder ? [] : (session.icons || []),
                      _placeholder: isPlaceholder,
                    }}
                    isOpen={openSession === session.id}
                    onToggle={() => toggleSession(session.id)}
                  />
                );
              })}
              {!dayHidden && visibleRaw.length === 0 && (
                <p className="text-gray-500 text-center py-8 italic">
                  No sessions match this filter.
                </p>
              )}
              {!dayHidden && (!day.sessions || day.sessions.length === 0) && (
                <p className="text-gray-500 text-center py-8 italic">
                  No sessions scheduled yet. Add sessions in the Admin Panel.
                </p>
              )}
            </div>
            )}
          </div>
        </section>
        );
      })}

      {/* Fallback: Show old hardcoded sections if no CMS days */}
      {(!cmsContent?.days || cmsContent.days.length === 0) && (
        <>
          {/* Day 1 Fallback */}
          <section id="day-1" className="py-12 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#00457f]/30"></div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#00457f]">
                  Day 1
                </h2>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#00457f]/30"></div>
              </div>
              <div className="space-y-4">
                {(programData.april1 || []).map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    isOpen={openSession === session.id}
                    onToggle={() => toggleSession(session.id)}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Day 2 Fallback */}
          <section id="day-2" className="py-12 bg-white">
            <div className="max-w-4xl mx-auto px-6">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#00457f]/30"></div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#00457f]">
                  Day 2
                </h2>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#00457f]/30"></div>
              </div>

              {/* Morning */}
              <div className="mb-12">
                <h3 className="text-lg font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-8 h-px bg-gray-300"></span>
                  Morning
                </h3>
                <div className="space-y-4">
                  {(programData.april2?.morning || []).map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      isOpen={openSession === session.id}
                      onToggle={() => toggleSession(session.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Midday */}
              <div className="mb-12">
                <h3 className="text-lg font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-8 h-px bg-gray-300"></span>
                  Midday
                </h3>
                <div className="space-y-4">
                  {(programData.april2?.midday || []).map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      isOpen={openSession === session.id}
                      onToggle={() => toggleSession(session.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Afternoon */}
              <div className="mb-12">
                <h3 className="text-lg font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-8 h-px bg-gray-300"></span>
                  Afternoon
                </h3>
                <div className="space-y-4">
                  {(programData.april2?.afternoon || []).map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      isOpen={openSession === session.id}
                      onToggle={() => toggleSession(session.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Evening */}
              <div>
                <h3 className="text-lg font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-8 h-px bg-gray-300"></span>
                  Evening
                </h3>
                <div className="space-y-4">
                  {(programData.april2?.evening || programData.april2?.closing || []).map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      isOpen={openSession === session.id}
                      onToggle={() => toggleSession(session.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Logistics */}
      <section id="logistics" className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#00457f]/30"></div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#00457f]">
              Logistics & Helpful Information
            </h2>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#00457f]/30"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {logistics.map((item) => (
              <LogisticsCard
                key={item.id}
                item={item}
                isOpen={openLogistics === item.id}
                onToggle={() => toggleLogistics(item.id)}
              />
            ))}
          </div>

          {/* After the Congress */}
          <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#00457f]" />
              After the Congress
            </h4>
            <p className="text-gray-700 mb-3">
              For dining, live music, and nightlife following the Congress:
            </p>
            <a 
              href="https://www.visitknoxville.com/things-to-do/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00457f] hover:underline font-medium"
            >
              Visit Knoxville – Things to Do →
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProgramPage;
